import { ApiResponse, LoginCredentials, User } from '../types';
import { apiClient } from './apiClient';

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: User;
  };
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ token: string; user: User }>> {
    try {
      console.log('AuthService: Attempting login with', credentials);
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
      console.log('AuthService: Raw API response', response);
      
      if (response.success && response.data) {
        // Store authentication data
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        
        console.log('AuthService: Login successful, storing token and user data');
        console.log('AuthService: Token:', response.data.token.substring(0, 20) + '...');
        console.log('AuthService: User:', response.data.user);
        
        return {
          success: true,
          message: response.message,
          data: response.data
        };
      }
      
      console.log('AuthService: Login response indicates failure', response);
      throw new Error(response.message || 'Login failed');
    } catch (error: any) {
      console.error('AuthService: Login error', error);
      
      // Re-throw 403 errors (shop suspended/expired) so they can be handled properly
      if (error.response?.status === 403) {
        throw error;
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Invalid credentials',
      };
    }
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
    }
  }

  async getMe(): Promise<User> {
    const response = await apiClient.get<{ success: boolean; data: User }>('/auth/me');
    return response.data;
  }

  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    return await apiClient.post('/auth/refresh');
  }

  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('user_data');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('user_data');
    return !!(token && user);
  }

  // Validate current session
  async validateSession(): Promise<boolean> {
    try {
      const response = await apiClient.get<{ data: { success: boolean } }>('/auth/validate');
      return response.data.success;
    } catch (error) {
      // Don't auto-logout on validation failure, let the caller handle it
      console.error('Session validation failed:', error);
      return false;
    }
  }

  // Password Reset Methods
  async sendResetCode(email: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; data?: any }>('/password/send-code', { email });
      return {
        success: response.success,
        message: response.message,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send reset code. Please try again.'
      };
    }
  }

  async verifyResetCode(email: string, code: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>('/password/verify-code', { email, code });
      return {
        success: response.success,
        message: response.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Invalid verification code'
      };
    }
  }

  async resetPassword(email: string, code: string, password: string, password_confirmation: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>('/password/reset', { 
        email, 
        code, 
        password, 
        password_confirmation 
      });
      return {
        success: response.success,
        message: response.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to reset password. Please try again.'
      };
    }
  }
}

export const authService = new AuthService();